/**
 * PaywallProductsPage
 *
 * Page component for managing paywall products.
 * Wraps PaywallProductsListView with state management and dialogs.
 * Supports both Stripe and Paddle payment providers.
 *
 * @module paywall-products/pages/PaywallProductsPage
 */

import React, { useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { PaywallProductsListView } from '../components/PaywallProductsListView';
import { parseProductsModeParam } from '../utils/modeParam';
import { PaywallProductDialog } from '../components/PaywallProductDialog';
import { PaddlePaywallProductDialog } from '../components/PaddlePaywallProductDialog';
import { usePaywallProducts } from '../hooks/usePaywallProducts';
import { useStripeConnect } from '@modules/stripe-connect';
import { usePaddleConnect } from '@modules/paddle-connect';
import { PAYWALL_PRODUCTS_TEST_IDS } from '@modules/paywall-products/testIds';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import { useGuideHighlight } from '@modules/guides/hooks/useGuideHighlight';

/**
 * PaywallProductsPage Component
 *
 * Main page for paywall products list with CRUD operations.
 * Supports both Stripe and Paddle payment providers.
 */
export function PaywallProductsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  // Read once at mount (AC-013); later tab clicks never write back to the URL.
  const [initialMode] = useState(() => parseProductsModeParam(location.search));
  useGuideHighlight();

  // Stripe connection status (separate test and live accounts)
  const { testStatus, liveStatus, loading: stripeLoading } = useStripeConnect(projectId || '');

  // Paddle connection status
  const { status: paddleStatus, loading: paddleLoading } = usePaddleConnect(projectId || '');

  // Products data
  const {
    products,
    loading,
    error,
    refresh,
    createProduct,
    updateProduct,
    deleteProduct,
  } = usePaywallProducts({
    projectId: projectId || '',
  });

  // Provider selection menu state
  const [providerMenuAnchor, setProviderMenuAnchor] = useState<null | HTMLElement>(null);

  // Dialog states - separate for Stripe and Paddle
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [paddleDialogOpen, setPaddleDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PaywallProduct | null>(null);
  const [defaultStripeMode, setDefaultStripeMode] = useState<'test' | 'live'>('test');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<PaywallProduct | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Open provider selection menu
  const handleCreateProductClick = useCallback((event: React.MouseEvent<HTMLElement>, mode: 'test' | 'live') => {
    // Store the mode from the active tab for dialog default
    setDefaultStripeMode(mode);

    // If only one provider connected, open that dialog directly
    // Stripe is connected if either test or live account is connected
    const stripeConnected = testStatus?.connected || liveStatus?.connected || false;
    const paddleConnected = paddleStatus?.connected ?? false;

    if (stripeConnected && !paddleConnected) {
      setEditingProduct(null);
      setStripeDialogOpen(true);
    } else if (paddleConnected && !stripeConnected) {
      setEditingProduct(null);
      setPaddleDialogOpen(true);
    } else {
      // Both or neither connected - show menu
      setProviderMenuAnchor(event.currentTarget);
    }
  }, [testStatus, liveStatus, paddleStatus]);

  const handleCloseProviderMenu = useCallback(() => {
    setProviderMenuAnchor(null);
  }, []);

  const handleSelectStripe = useCallback(() => {
    setEditingProduct(null);
    setStripeDialogOpen(true);
    handleCloseProviderMenu();
  }, [handleCloseProviderMenu]);

  const handleSelectPaddle = useCallback(() => {
    setEditingProduct(null);
    setPaddleDialogOpen(true);
    handleCloseProviderMenu();
  }, [handleCloseProviderMenu]);

  // Edit handler - opens appropriate dialog based on provider
  const handleEditProduct = useCallback((product: PaywallProduct) => {
    setEditingProduct(product);
    if (product.paymentProvider === 'paddle') {
      setPaddleDialogOpen(true);
    } else {
      // Default to Stripe for existing products without paymentProvider
      setStripeDialogOpen(true);
    }
  }, []);

  const handleCloseStripeDialog = useCallback(() => {
    setStripeDialogOpen(false);
    setEditingProduct(null);
  }, []);

  const handleClosePaddleDialog = useCallback(() => {
    setPaddleDialogOpen(false);
    setEditingProduct(null);
  }, []);

  const handleSaveProduct = useCallback(
    async (data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
      } else {
        await createProduct(data);
      }
      // Close both dialogs (only one should be open)
      handleCloseStripeDialog();
      handleClosePaddleDialog();
    },
    [editingProduct, createProduct, updateProduct, handleCloseStripeDialog, handleClosePaddleDialog]
  );

  const handleDeleteClick = useCallback((product: PaywallProduct) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!productToDelete) return;

    setDeleting(true);
    try {
      await deleteProduct(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } finally {
      setDeleting(false);
    }
  }, [productToDelete, deleteProduct]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  }, []);

  if (!projectId) {
    return null;
  }

  // Stripe is connected if either test or live account is connected
  const isStripeConnected = testStatus?.connected || liveStatus?.connected || false;
  const isPaddleConnected = paddleStatus?.connected ?? false;

  return (
    <Box sx={{ height: '100%' }}>
      <PaywallProductsListView
        products={products}
        loading={loading || stripeLoading || paddleLoading}
        error={error}
        isStripeConnected={isStripeConnected}
        isPaddleConnected={isPaddleConnected}
        onCreateProduct={handleCreateProductClick}
        onEditProduct={handleEditProduct}
        onDeleteProduct={handleDeleteClick}
        onRefresh={refresh}
        initialMode={initialMode ?? undefined}
      />

      {/* Provider Selection Menu */}
      <Menu
        anchorEl={providerMenuAnchor}
        open={Boolean(providerMenuAnchor)}
        onClose={handleCloseProviderMenu}
      >
        <MenuItem
          onClick={handleSelectStripe}
          disabled={!isStripeConnected}
          data-testid={PAYWALL_PRODUCTS_TEST_IDS.providerMenuStripe}
        >
          <ListItemIcon>
            <CreditCardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Create Stripe Product"
            secondary={!isStripeConnected ? 'Not connected' : undefined}
          />
        </MenuItem>
        <MenuItem
          onClick={handleSelectPaddle}
          disabled={!isPaddleConnected}
          data-testid={PAYWALL_PRODUCTS_TEST_IDS.providerMenuPaddle}
        >
          <ListItemIcon>
            {/* Paddle icon as simple SVG */}
            <Box
              component="span"
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#FFDE59',
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 'bold',
                color: '#1B1B1B',
              }}
            >
              P
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Create Paddle Product"
            secondary={!isPaddleConnected ? 'Not connected' : undefined}
          />
        </MenuItem>
      </Menu>

      {/* Stripe Create/Edit Dialog */}
      <PaywallProductDialog
        open={stripeDialogOpen}
        product={editingProduct}
        projectId={projectId}
        defaultMode={defaultStripeMode}
        onClose={handleCloseStripeDialog}
        onSave={handleSaveProduct}
      />

      {/* Paddle Create/Edit Dialog */}
      <PaddlePaywallProductDialog
        open={paddleDialogOpen}
        product={editingProduct}
        projectId={projectId}
        onClose={handleClosePaddleDialog}
        onSave={handleSaveProduct}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PaywallProductsPage;
